import type { TeactNode } from '../../lib/teact/teact';
import React from '../../lib/teact/teact';

import type { ApiChain, ApiNft } from '../../api/types';
import type { Account, AccountType } from '../../global/types';
import type { AccountBalance } from '../../hooks/useAccountsBalances';

import buildClassName from '../../util/buildClassName';
import { getOrderedAccountChains } from '../../util/chain';
import { getTelegramAvatarUrlFromDomain } from '../../util/dns';
import { formatAccountAddresses } from '../../util/formatAccountAddress';
import { formatCurrency } from '../../util/formatNumber';
import isViewAccount from '../../util/isViewAccount';

import useLang from '../../hooks/useLang';

import CustomCardPreview from '../main/modals/accountSelector/CustomCardPreview';
import IconWithTooltip from '../ui/IconWithTooltip';
import SensitiveData from '../ui/SensitiveData';
import WalletAvatar from '../ui/WalletAvatar';

import styles from './AccountRowContent.module.scss';

export interface AccountRowInnerProps {
  accountId: string;
  byChain: Account['byChain'];
  visibleChains?: ApiChain[];
  accountType: AccountType;
  title?: string;
  isTestnet?: boolean;
  isRecoveryRequired?: true;
  balanceData?: AccountBalance;
  cardBackgroundNft?: ApiNft;
  isSensitiveDataHidden?: true;
  suffixIcon?: TeactNode;
  avatarClassName?: string;
  avatarUrl?: string;
}

/**
 * Renders just the inner content of an account row (avatar, info, balance, suffix).
 * Does NOT include a wrapper div - meant to be used inside parent's wrapper.
 */
function AccountRowInner({
  accountId,
  byChain,
  visibleChains,
  accountType,
  title,
  isTestnet,
  isRecoveryRequired,
  balanceData,
  cardBackgroundNft,
  isSensitiveDataHidden,
  suffixIcon,
  avatarClassName,
  avatarUrl,
}: AccountRowInnerProps) {
  const lang = useLang();
  const isHardware = accountType === 'hardware';
  const isView = isViewAccount(accountType);
  const chains = visibleChains ?? getOrderedAccountChains(byChain);
  const formattedAddress = formatAccountAddresses(byChain, chains, chains.length === 1 ? 'medium' : 'small');
  const resolvedAvatarUrl = avatarUrl ?? getTelegramAvatarUrlFromDomain(byChain.ton?.domain);

  return (
    <>
      <WalletAvatar
        title={title}
        accountId={accountId}
        className={buildClassName(styles.avatar, avatarClassName)}
        imageUrl={resolvedAvatarUrl}
      />

      <div className={styles.info}>
        <div className={styles.titleRow}>
          <span className={styles.title}>{title}</span>
          {cardBackgroundNft && (
            <CustomCardPreview nft={cardBackgroundNft} className={styles.nftIndicator} />
          )}
          {isRecoveryRequired && (
            <IconWithTooltip
              type="danger"
              size="small"
              message={lang('$enclave_recovery_required_tooltip')}
              iconClassName={styles.recoveryIcon}
              canHoverOnTooltip
            />
          )}
        </div>
        <div className={styles.address}>
          {isTestnet && <i className={buildClassName(styles.icon, 'icon-testnet')} aria-hidden />}
          {isHardware && <i className={buildClassName(styles.icon, 'icon-ledger')} aria-hidden />}
          {isView && <i className={buildClassName(styles.icon, 'icon-eye-filled')} aria-hidden />}
          {formattedAddress}
        </div>
      </div>

      {balanceData && (
        <SensitiveData
          isActive={isSensitiveDataHidden}
          rows={2}
          min={5}
          max={10}
          seed={title || ''}
          cellSize={8}
          className={styles.balanceWrapper}
          contentClassName={styles.balanceContainer}
          align="right"
        >
          <div className={styles.balance}>
            {formatCurrency(balanceData.value, balanceData.currencySymbol)}
          </div>
        </SensitiveData>
      )}

      {suffixIcon}
    </>
  );
}

export default AccountRowInner;
